import { ClipboardList, Plus, Star } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { JOB_STATUS_TONES, useJobs } from '@/hooks/jobs';
import { formatDay, formatDuration, formatWallTime } from '@/lib/dates';
import { JobCreateDialog } from '@/pages/admin/jobs/JobCreateDialog';
import { JOB_STATUS_LABELS, type JobListItem, type JobStatusValue } from '@shared/schemas/job';
import { DEFAULT_PAGE_SIZE, RECORD_SORTS } from '@shared/schemas/pagination';
import { SortSelect } from '@/components/ui/sort-select';
import type { RecordSort } from '@shared/schemas/pagination';

/*
 * /admin/jobs — ROADMAP.md §4.3.
 *
 * Everything on this screen is a display value: `statusLabel` and
 * `serviceTypeName` come from the server, never the slugs (P5). Below 768px
 * the DataTable stacks to cards (P6 — the prototype's tables scroll sideways
 * on a phone).
 */

const STATUS_FILTERS: { value: JobStatusValue | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: JOB_STATUS_LABELS.pending },
  { value: 'scheduled', label: JOB_STATUS_LABELS.scheduled },
  { value: 'in_progress', label: JOB_STATUS_LABELS.in_progress },
  { value: 'completed', label: JOB_STATUS_LABELS.completed },
  { value: 'cancelled', label: JOB_STATUS_LABELS.cancelled },
];

export function JobsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<JobStatusValue | 'all'>('all');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<RecordSort>('newest');
  const [creating, setCreating] = useState(false);

  const jobs = useJobs({ sort, page, search, status });

  const columns: Column<JobListItem>[] = [
    {
      key: 'job',
      header: 'Job',
      hideLabelOnCard: true,
      /*
        **The job number is a real link, because `onRowClick` is mouse-only.**
        A `<tr>` with a click handler has no tabindex, no role and no key
        handler, so until the audit of 2026-08-06 there was no way to open a job
        from this screen without a mouse — WCAG 2.1.1, Level A. 13.3's sweep
        could not see it: that spec asks whether focusable controls draw a focus
        ring, and an unfocusable row is not a focusable control.

        A link in the identifier cell is the fix rather than a focusable row,
        because it is the pattern /admin/invoices already uses, and because a
        `<tr role="button">` would lie to a screen reader about the table.
        `onRowClick` stays as the mouse convenience it always was.
      */
      render: (row) => (
        <div className="min-w-0">
          <span className="flex items-center gap-1.5 text-body font-medium text-ink-900">
            {row.isHighPriority ? (
              <Star
                aria-hidden="true"
                className="size-4 shrink-0 fill-honey-400 text-status-pending-text"
              />
            ) : null}
            <Link to={`/admin/jobs/${row.id}`} className="underline underline-offset-2">
              {row.jobNumber}
            </Link>
            {row.isHighPriority ? <span className="sr-only"> (high priority)</span> : null}
          </span>
          <span className="mt-0.5 block truncate text-meta text-ink-500">{row.clientName}</span>
        </div>
      ),
    },
    {
      key: 'service',
      header: 'Service',
      render: (row) => (
        <div className="min-w-0 text-body">
          <span className="block">{row.serviceTypeName}</span>
          {row.categoryName ? (
            <span className="block text-meta text-ink-500">{row.categoryName}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'when',
      header: 'Scheduled',
      render: (row) => (
        <div className="whitespace-nowrap text-body">
          <span className="block">{formatDay(row.scheduledDate)}</span>
          <span className="block text-meta text-ink-500">
            {formatWallTime(row.scheduledTime)} · {formatDuration(row.estimatedDurationMinutes)}
          </span>
        </div>
      ),
    },
    {
      key: 'address',
      header: 'Address',
      render: (row) => <span className="text-body">{row.addressSnapshot}</span>,
    },
    {
      key: 'crew',
      header: 'Crew',
      render: (row) =>
        row.assignees.length > 0 ? (
          <span className="text-body">{row.assignees.map((entry) => entry.name).join(', ')}</span>
        ) : (
          // A fact and its consequence, not a dash (P1).
          <span className="text-meta text-ink-500">Nobody yet</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge label={row.statusLabel} tone={JOB_STATUS_TONES[row.status]} />,
    },
  ];

  return (
    <AdminPage
      title="Jobs"
      description="Every job, who is on it, and where it is in its life."
      actions={
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" />
          Create job
        </Button>
      }
    >
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label="Search jobs"
          placeholder="Job number, client or address"
          className="sm:w-72"
        />

        <FilterPills
          label="Filter jobs by status"
          options={STATUS_FILTERS}
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
        />
      </div>

      {/* The order control. A select rather than clickable headers —

          the list becomes cards below 768px and a card has no headers. */}

      <div className="mb-3 flex justify-end">
        <SortSelect
          label="Sort jobs"

          options={RECORD_SORTS}

          value={sort}

          onChange={(next) => {
            setSort(next);

            // Page 1, or you are on page 3 of a differently ordered list.

            setPage(1);
          }}
        />
      </div>

      <DataTable
        caption="Jobs, their schedule, crew and status"
        columns={columns}
        rows={jobs.data?.data ?? []}
        rowKey={(row) => row.id}
        // On a phone the whole card is the link — see DataTable's `rowHref`.
        rowHref={(row) => `/admin/jobs/${row.id}`}
        isLoading={jobs.isPending}
        error={jobs.error}
        onRetry={() => {
          void jobs.refetch();
        }}
        onRowClick={(row) => {
          void navigate(`/admin/jobs/${row.id}`);
        }}
        empty={
          <EmptyState
            icon={ClipboardList}
            title={search || status !== 'all' ? 'No jobs match those filters.' : 'No jobs yet.'}
            hint={
              search || status !== 'all'
                ? 'Try clearing the search or showing all statuses.'
                : 'A job is one visit to one property. Creating one copies the property’s checklist onto it.'
            }
            action={
              search || status !== 'all' ? null : (
                <Button
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  <Plus aria-hidden="true" />
                  Create job
                </Button>
              )
            }
          />
        }
      />

      {jobs.data ? (
        <Pagination
          page={jobs.data.page}
          pageSize={jobs.data.pageSize || DEFAULT_PAGE_SIZE}
          total={jobs.data.total}
          onPageChange={setPage}
          itemNoun="jobs"
        />
      ) : null}

      <JobCreateDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(created) => {
          void navigate(`/admin/jobs/${created.id}`);
        }}
      />
    </AdminPage>
  );
}
